import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { BasicPlanSection } from "@/components/dashboard/BasicPlanSection";
import { ServiceFormDialog } from "@/components/service-form/ServiceFormDialog";
import ServiceRequestsView from "@/components/service-requests/ServiceRequestsView";
import { ServicesSection } from "@/components/dashboard/ServicesSection";
import CustomersView from "@/components/customers/CustomersView";
import { Marketing } from "@/components/dashboard/Marketing";
import ServiceCategories from "@/pages/ServiceCategories";
import AvailabilitySettings from "@/pages/AvailabilitySettings";
import SettingsTab from "@/components/dashboard/SettingsTab";
import UpcomingAppointments from "@/components/dashboard/UpcomingAppointments";
import { Button } from "@/components/ui/button";
import { Eye, Share2 } from "lucide-react";
import { supabase, generateServiceLink } from "@/integrations/supabase/client";
import { Profile, Service } from "@/types/dashboard";
import { fetchServices, deleteService, updateServiceCategory } from "@/utils/serviceUtils";
import TabHeading from "./TabHeading";
import { useAnalytics } from "@/hooks/useAnalytics";
import { ActionRequiredSection } from "@/components/dashboard/ActionRequiredSection";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/use-toast";
import { ProfileCompletenessCard } from "@/components/dashboard/ProfileCompletenessCard";

interface DashboardContentProps {
  activeView: string;
  profile: Profile | null;
  searchQuery: string;
  userCategories: any[];
  showCreateService: boolean;
  selectedSettingsSection: string;
  selectedRequestsView: string;
  hasRequests: boolean;
  services: Service[];
  viewMode?: 'list' | 'detail';
  setShowCreateService: (show: boolean) => void;
  onViewSite: () => void;
  onWhatsAppShare: () => void;
  onSettingsSectionChange?: (section: string) => void;
  refreshServices: () => Promise<void>;
  refreshCategories: () => Promise<void>;
  className?: string;
}

const ShareIcon = ({ isTelegramPreferred }: { isTelegramPreferred: boolean }) => {
  if (isTelegramPreferred) {
    return (
      <img 
        src="/icons8-telegram.svg" 
        alt="Telegram" 
        className="w-4 h-4" 
        style={{ filter: 'brightness(0) invert(1)' }} // Make icon white to match button text
      />
    );
  } else {
    return (
      <img 
        src="/icons8-whatsapp.svg" 
        alt="WhatsApp" 
        className="w-4 h-4" 
        style={{ filter: 'brightness(0) invert(1)' }} // Make icon white to match button text
      />
    );
  }
};

export const DashboardContent = ({
  activeView,
  profile,
  searchQuery,
  userCategories,
  showCreateService,
  selectedSettingsSection,
  selectedRequestsView,
  hasRequests,
  services,
  viewMode = 'list',
  setShowCreateService,
  onViewSite,
  onWhatsAppShare,
  onSettingsSectionChange,
  refreshServices,
  refreshCategories,
  className
}: DashboardContentProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { trackAction } = useAnalytics({ component: "Dashboard" });
  const [serviceToEdit, setServiceToEdit] = useState<Service | null>(null);
  const [localServices, setLocalServices] = useState<Service[]>(services || []);
  const [serviceFormOpen, setServiceFormOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setLocalServices(services || []);
  }, [services]);

  useEffect(() => {
    setServiceFormOpen(showCreateService);
  }, [showCreateService]);

  const searchParams = new URLSearchParams(location.search);
  const editServiceId = searchParams.get('edit');

  useEffect(() => {
    if (editServiceId) {
      const service = localServices.find(s => s.id === editServiceId);
      if (service) {
        setServiceToEdit(service);
        setServiceFormOpen(true);
      }
    } else {
      setServiceToEdit(null);
    }
  }, [editServiceId, localServices]);

  // if (!profile) {
  //   return (
  //     <div className={cn("p-4", className)}>
  //       <div className="flex flex-col items-center justify-center min-h-[400px]">
  //         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gebeya-pink mb-4"></div>
  //         <p className="text-gray-500">Loading your profile information...</p>
  //       </div>
  //     </div>
  //   );
  // }

  const hasValidServices = Array.isArray(services) && services.length >= 0;
  if (!hasValidServices) {
    return (
      <div className={cn("p-4", className)}>
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gebeya-pink mb-4"></div>
          <p className="text-gray-500">Loading your services...</p>
        </div>
      </div>
    );
  }

  const preferredMessaging = profile?.preferred_messaging || 'whatsapp';
  const isTelegramPreferred = preferredMessaging === 'telegram';
  const hasTelegramConnected = Boolean(profile?.telegram_chat_id);

  const shareButtonClassName = isTelegramPreferred
    ? "bg-[#0EA5E9] hover:bg-[#1EAEDB] text-white flex items-center gap-1 w-full sm:w-auto"
    : "bg-[#25D366] hover:bg-[#128C7E] text-white flex items-center gap-1 w-full sm:w-auto";

  const filteredServices = localServices.filter(service =>
    service.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleShare = async () => {
    if (profile?.id) {
      const shareUrl = `https://rnertcqmhnpibmxyqkvc.supabase.co/functions/v1/share-link?u=${profile.id}&r=true`;
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Check out my services',
            text: 'Take a look at what I have to offer!',
            url: shareUrl,
          });
        } catch (error) {
          console.log('Error sharing:', error);
        }
      } else {
        navigator.clipboard.writeText(shareUrl);
        toast({
          title: "Link copied!",
          description: "Your service link has been copied to clipboard.",
        });
      }
    }
  };

  const handleViewSite = () => {
    trackAction('view_service_page');
    
    if (profile?.service_page_link) {
      const username = profile.service_page_link.includes('/') 
        ? profile.service_page_link.split('/').pop() 
        : profile.service_page_link;
        
      const serviceUrl = `${window.location.origin}/services/${username}`;
      window.open(serviceUrl, '_blank', 'noopener,noreferrer');
    } else if (profile?.id) {
      const serviceLink = generateServiceLink(profile.id);
      const username = serviceLink.includes('/') 
        ? serviceLink.split('/').pop() 
        : serviceLink;
        
      const serviceUrl = `${window.location.origin}/services/${username}`;
      window.open(serviceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleEditService = () => {
    trackAction('edit_service');
    
    if (localServices.length > 0) {
      navigate('/dashboard/services');
    } else {
      setServiceFormOpen(true);
      setShowCreateService(true);
    }
  };

  const handleServiceFormClose = () => {
    console.log("handleServiceFormClose called");
    setServiceFormOpen(false);
    setShowCreateService(false);
    setServiceToEdit(null);
    
    const newUrl = '/dashboard/services';
    navigate(newUrl, { replace: true });
    
    refreshServices();
  };

  const getSettingsDescription = () => {
    switch (selectedSettingsSection) {
      case 'general':
        return t('settings.generalSettings');
      case 'business':
        return t('settings.businessSettings');
      case 'notifications':
        return t('settings.notificationSettings');
      case 'availability':
        return t('dashboard.availabilityDescription');
      default:
        return t('settings.generalSettings');
    }
  };

  return (
    <div className={cn("pt-2 md:pt-4 bg-white", className)}>
      <div className="px-2 md:px-8 pb-2 md:pb-8">
        <div className="flex flex-col items-center mb-3 md:mb-6 px-2 md:px-4 mt-16">
          <h1 className="text-xl md:text-2xl font-bold mb-2 md:mb-4 dashboard-business-name">
            {profile?.company_name || 
             (profile?.first_name && profile?.last_name 
              ? `${profile.first_name} ${profile.last_name}` 
              : profile?.first_name || profile?.last_name || t('dashboard.myBusiness'))}
          </h1>
          <div className="flex gap-2">
            <Button 
              onClick={handleViewSite} 
              variant="outline"
              size="sm"
              className="flex items-center gap-1 tour-view-site"
            >
              <Eye className="h-4 w-4" />
              <span>{t('dashboard.viewMySite')}</span>
            </Button>
            <Button 
              onClick={onWhatsAppShare}
              size="sm"
              className={`${shareButtonClassName} tour-share`}
            >
              <ShareIcon isTelegramPreferred={isTelegramPreferred} />
              <span>{t('dashboard.share')}</span>
            </Button>
          </div>
        </div>

        {/* Profile Completeness Widget */}
        <ProfileCompletenessCard services={services} />

        {activeView === "overview" && (
          <>
            <div className="px-2 md:px-0">
              <TabHeading 
                title={t('dashboard.overview')} 
                description={t('dashboard.overviewDescription')} 
              />
            </div>
            <DashboardOverview 
              userId={profile?.id}
            />
          </>
        )}

        {activeView === "services" && (
          <>
            <TabHeading 
              title={t('navigation.services')} 
              description={t('services.description')} 
            />
            <ServicesSection
              services={filteredServices}
              searchQuery={searchQuery}
              setSearchQuery={() => {}}
              userCategories={userCategories}
              onDeleteService={async (serviceId) => {
                const result = await deleteService(serviceId);
                if (result) {
                  await refreshServices();
                }
                return result;
              }}
              onUpdateServiceCategory={async (serviceId, categoryId) => {
                const result = await updateServiceCategory(serviceId, categoryId);
                if (result) {
                  await refreshServices();
                }
                return result;
              }}
              currency={profile?.currency}
            />
          </>
        )}

        {activeView === "service-requests" && (
          <>
            <TabHeading 
              title={t('navigation.requests')} 
              description={t('common.manageServiceRequests')} 
            />
            <ServiceRequestsView 
              initialFilter={selectedRequestsView as "all" | "pending" | "scheduled" | "completed"}
              viewMode={viewMode}
            />
          </>
        )}

        {activeView === "customers" && (
          <>
            <TabHeading 
              title={t('navigation.customers')} 
              description={t('common.manageCustomerRelationships')} 
            />
            <CustomersView />
          </>
        )}

        {activeView === "marketing" && (
          <>
            <TabHeading 
              title={t('navigation.marketing')} 
              description={t('common.promoteBusinessServices')} 
            />
            <Marketing />
          </>
        )}

        {activeView === "settings" && (
          <>
            <TabHeading 
              title={`${selectedSettingsSection.charAt(0).toUpperCase() + selectedSettingsSection.slice(1)} ${t('navigation.settings')}`} 
              description={getSettingsDescription()} 
            />
            <SettingsTab 
              initialSection={selectedSettingsSection} 
              onSectionChange={onSettingsSectionChange}
            />
          </>
        )}

        {activeView === "categories" && (
          <>
            <TabHeading 
              title={t('dashboard.serviceCategories')} 
              description={t('dashboard.serviceCategoriesDescription')} 
            />
            <div className="bg-white rounded-lg shadow-lg p-6">
              <ServiceCategories />
            </div>
          </>
        )}

        {activeView === "availability" && (
          <>
            <TabHeading 
              title={t('dashboard.availabilityTitle')} 
              description={t('dashboard.availabilityDescription')} 
            />
            <div className="bg-white rounded-lg shadow-lg p-6">
              <AvailabilitySettings />
            </div>
          </>
        )}

        <ServiceFormDialog
          open={serviceFormOpen}
          onOpenChange={(open) => {
            setServiceFormOpen(open);
            if (!open) handleServiceFormClose();
          }}
          onSuccess={handleServiceFormClose}
          initialData={serviceToEdit}
          refreshServices={refreshServices}
        />
      </div>
    </div>
  );
};

interface DashboardOverviewProps {
  userId: string | undefined;
}

const DashboardOverview = ({ userId }: DashboardOverviewProps) => {
  return (
    <>
      <ActionRequiredSection userId={userId} />
      <BasicPlanSection />
      <div className="mt-4 md:mt-8">
        <UpcomingAppointments userId={userId || ''} />
      </div>
    </>
  );
};
